/**
 * @fileoverview Manages undo and redo operations for code rewrites, maintaining a history of changes in a JSONL file. Provides functionality to track, record, undo, and redo code modifications across multiple files, supporting operations like undoing the most recent changes, preserving line-based edit history, and tracking changes by operation.
 */

import { diff_block, info } from '@iterator/shared-node/util/logging'
import { existsSync } from 'fs'
import { z } from 'zod'
import { getAwolfPaths, readJsonlFile, writeJsonlFile } from './awolf_dir'
import { IdProvider } from './id_provider'
import {
  applyRewritesToFiles,
  invertRewrite,
  RewriteMode,
  rewriteToDiff,
  RewriteWithPath,
} from './rewrite'

/**
 * History file format is a jsonl file where each row is a Rewrite. An operation (eg. a call to generate code)
 * can have many rewrites.
 *
 * Rewrites include line numbers that are relative the file at the time of the edit. That means if an older change
 * is undone, the line numbers of later changes will change.
 *
 * Example history file:
 * { id: 'abcd', operationId: 'ered', timestamp: 1724409600, filePath: 'path/to/file1.ts', rewrite: { ... } }
 * { id: 'adfd', operationId: 'ered', timestamp: 1724409600, filePath: 'path/to/file1.ts', rewrite: { ... } }
 * { id: 'dfsf', operationId: 'sdfs', timestamp: 1724409602, filePath: 'path/to/file2.ts', rewrite: { ... } }
 *
 * Operations we want to support:
 * - undo the most recent operation (top of the stack)
 * - undo a file in the most recent operation
 * - undo a single rewrite in the most recent operation
 * - redo anything that was undone
 *
 * When undoing a set of re-writes, they will be removed from history and saved as a single operation
 * in a redo log. Redo will always redo the entire operation.
 *
 * What should we do when the underlaying code has changed? Eg. the lines do not match the original lines.
 * - fail all <- will do this for now and can add more sophisticated handling later
 * - undo until the code matches again
 * - drop the failing rewrite from the operation
 *
 */

export type HistoricalChange = {
  id: string
  operationId: string
  timestamp: number
  rewrite: RewriteWithPath
}

export const RewriteWithPathSchema = z.object({
  type: z.enum(['editFile', 'createFile', 'removeFile']),
  filePath: z.string(),
  originalLineRange: z.object({
    start: z.number(),
    end: z.number(),
  }),
  originalLines: z.array(z.string()),
  newLineRange: z.object({
    start: z.number(),
    end: z.number(),
  }),
  newLines: z.array(z.string()),
})

export const HistoricalChangeSchema = z.object({
  id: z.string(),
  operationId: z.string(),
  timestamp: z.number(),
  rewrite: RewriteWithPathSchema,
})

export function toJsonl(changes: HistoricalChange[]) {
  if (changes.length === 0) {
    return ''
  }
  return changes.map((c) => JSON.stringify(c)).join('\n')
}

export class UndoRedo {
  private historyFile: string
  private redoFile: string
  private idProvider: IdProvider

  constructor(directory: string, idProvider: IdProvider = new IdProvider([])) {
    const paths = getAwolfPaths(directory)
    this.historyFile = paths.history
    this.redoFile = paths.redo
    this.idProvider = idProvider

    if (!existsSync(this.historyFile)) {
      writeJsonlFile(this.historyFile, [])
    }
    if (!existsSync(this.redoFile)) {
      writeJsonlFile(this.redoFile, [])
    }
  }

  getUndoStack(): HistoricalChange[] {
    return this.load(this.historyFile)
  }

  getRedoStack(): HistoricalChange[] {
    return this.load(this.redoFile)
  }

  async record(rewrites: RewriteWithPath[]) {
    const operationId = this.idProvider.nextId()
    const timestamp = Date.now()
    let undoStack = this.getUndoStack()
    for (const rewrite of rewrites) {
      undoStack.push({
        id: this.idProvider.nextId(),
        operationId,
        timestamp,
        rewrite,
      })
    }
    this.save(this.historyFile, undoStack)
  }

  async undo(mode: RewriteMode = 'strict') {
    this.popOperationAndApply(this.historyFile, this.redoFile, mode, true)
  }

  async redo(mode: RewriteMode = 'strict') {
    this.popOperationAndApply(this.redoFile, this.historyFile, mode, false)
  }

  clear() {
    writeJsonlFile(this.historyFile, [])
    writeJsonlFile(this.redoFile, [])
  }

  async printHistory(n: number = 1, stackType: 'undo' | 'redo' = 'undo'): Promise<void> {
    const stack = stackType === 'undo' ? this.getUndoStack() : this.getRedoStack()
    const operations = this.getTopNOperations(stack, n)
    operations.forEach((operation) => this.prettyPrintOperation(operation))
  }

  private getTopNOperations(
    stack: HistoricalChange[],
    n: number,
  ): { operationId: string; changes: HistoricalChange[] }[] {
    if (n === 0 || stack.length === 0) {
      return []
    }

    const result: { operationId: string; changes: HistoricalChange[] }[] = []

    while (stack.length > 0 && result.length < n) {
      const operation = this.popOperation(stack)
      if (operation) {
        result.push(operation)
      }
    }
    return result
  }

  private prettyPrintOperation(operation: { operationId: string; changes: HistoricalChange[] }) {
    info('')
    info(
      `operation_id=${operation.operationId} (${new Date(operation.changes[0]!.timestamp).toISOString()})`,
    )
    for (const change of operation.changes) {
      diff_block(
        `${change.rewrite.filePath}:${change.rewrite.newLineRange.start} (id=${change.id})`,
        rewriteToDiff(change.rewrite),
      )
    }
    info('')
  }

  private popOperation(
    stack: HistoricalChange[],
  ): { changes: HistoricalChange[]; operationId: string } | undefined {
    const lastChange = stack.at(-1)
    if (!lastChange) {
      return undefined
    }

    const changes: HistoricalChange[] = []
    while (stack.at(-1)?.operationId === lastChange.operationId) {
      const historicalChange = stack.pop()!
      changes.push(historicalChange)
    }
    return { changes, operationId: lastChange.operationId }
  }

  private popOperationAndApply(
    popFromFilePath: string,
    addToFilePath: string,
    mode: RewriteMode,
    shouldInvert: boolean,
  ) {
    let stack = this.load(popFromFilePath)
    const lastChange = stack.at(-1)
    if (!lastChange) {
      return
    }

    const timestamp = Date.now()
    const changes: HistoricalChange[] = []
    while (stack.at(-1)?.operationId === lastChange.operationId) {
      const historicalChange = stack.pop()!
      changes.push({
        ...historicalChange,
        timestamp,
      })
    }

    applyRewritesToFiles(
      changes.map((c) => (shouldInvert ? invertRewrite(c.rewrite) : c.rewrite)),
      mode,
    )

    const addToStack = this.load(addToFilePath)
    addToStack.push(...changes)

    this.save(popFromFilePath, stack)
    this.save(addToFilePath, addToStack)
  }

  async undoFile(_filePath: string) {
    throw new Error('Not implemented')
  }

  async undoRewrite(_id: number) {
    throw new Error('Not implemented')
  }

  private load(filePath: string): HistoricalChange[] {
    return readJsonlFile<HistoricalChange>(filePath)
  }

  private save(filePath: string, changes: HistoricalChange[]) {
    writeJsonlFile(filePath, changes)
  }
}
